import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { requireSession } from '@/lib/ensureSession';

/**
 * Учёт воды за сегодня.
 *
 * ⚠️ Границы дня считаются ЛОКАЛЬНО и уходят в запрос диапазоном, а не через ::date
 * на сервере. Приведение даты по UTC — источник трёх багов, которые мы уже ловили:
 * запись после полуночи по Москве попадала во вчерашний день.
 */
export function useWater() {
  const { profile } = useUserStore();
  const [ml, setMl] = useState(0);
  const [foodMl, setFoodMl] = useState(0);
  const [loading, setLoading] = useState(true);

  // Норма: 30 мл на кг — общепринятый ориентир. Если вес неизвестен, берём 2 литра.
  const goal =
    profile?.water_goal_ml ??
    (profile?.weight_kg ? Math.round((profile.weight_kg * 30) / 50) * 50 : 2000);

  const refetch = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);

    // Норма 30 мл/кг считается по ОБЩЕЙ жидкости, поэтому вода из еды идёт в зачёт:
    // суп, овощи и фрукты дают заметную долю, и без них человек, который ест много
    // овощей, всегда видел бы недобор.
    const [drunk, food] = await Promise.all([
      supabase
        .from('water_log')
        .select('amount_ml')
        .eq('user_id', profile.id)
        .gte('logged_at', start.toISOString())
        .lt('logged_at', end.toISOString()),
      supabase
        .from('meal_items')
        .select('water_ml, meals!inner(consumed_at, user_id)')
        .eq('meals.user_id', profile.id)
        .gte('meals.consumed_at', start.toISOString())
        .lt('meals.consumed_at', end.toISOString()),
    ]);

    setMl((drunk.data ?? []).reduce((s, r: any) => s + (r.amount_ml || 0), 0));
    setFoodMl((food.data ?? []).reduce((s, r: any) => s + (r.water_ml || 0), 0));
    setLoading(false);
  }, [profile?.id]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Возврат в приложение — перечитываем: человек мог пить с другого устройства.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  /** Добавить порцию. Отрицательное значение отменяет последнюю запись. */
  const add = useCallback(
    async (amount: number) => {
      if (!profile?.id) return;
      if (!(await requireSession())) return;

      if (amount > 0) {
        setMl((v) => v + amount); // рисуем сразу, не дожидаясь сервера
        const { error } = await supabase
          .from('water_log')
          .insert({ user_id: profile.id, amount_ml: amount });
        if (error) {
          setMl((v) => v - amount); // не записалось — возвращаем как было
          console.error('[water] insert failed:', error.message);
        }
        return;
      }

      // Отмена: убираем последнюю запись за сегодня.
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const { data } = await supabase
        .from('water_log')
        .select('id, amount_ml')
        .eq('user_id', profile.id)
        .gte('logged_at', start.toISOString())
        .order('logged_at', { ascending: false })
        .limit(1);

      const last = data?.[0] as any;
      if (!last) return;
      setMl((v) => Math.max(0, v - last.amount_ml));
      const { error } = await supabase.from('water_log').delete().eq('id', last.id);
      if (error) {
        setMl((v) => v + last.amount_ml);
        console.error('[water] delete failed:', error.message);
      }
    },
    [profile?.id],
  );

  return { ml, foodMl, total: ml + foodMl, goal, loading, add, refetch };
}
